/**
 * PromoRowCard — compact promo card for the home scroll row.
 * A shopfront tile led by its banner (or per-kind gradient + icon) at the
 * banner's own 16:9, with the reward headline over it and a compact title,
 * description and CTA below. No claim logic or self-fetching — the full PromoCard on
 * /promos owns that. onClick navigates.
 */
import {
  Gift,
  TrendingUp,
  RotateCw,
  BadgePercent,
  CalendarCheck,
  Coins,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button.js';
import { promoBanner, promoTitle, type Promotion } from '@/features/promo/api.js';
import { rewardHeadlineFor } from '@/features/promo/headline.js';
import { useFormatMoney, useMoneyCurrency } from '@/components/money/MoneyProvider.js';

const KIND_THEME: Record<string, { gradient: string; Icon: LucideIcon }> = {
  welcome:         { gradient: 'var(--art-1)', Icon: Gift },
  first_deposit:   { gradient: 'var(--art-2)', Icon: TrendingUp },
  reload:          { gradient: 'var(--art-3)', Icon: RotateCw },
  cashback:        { gradient: 'var(--art-4)', Icon: BadgePercent },
  turnover_rebate: { gradient: 'var(--art-5)', Icon: Coins },
  daily_checkin:   { gradient: 'var(--art-6)', Icon: CalendarCheck },
};

const FALLBACK = KIND_THEME['reload'];

interface Props {
  promo: Promotion;
  onClick: () => void;
}

export function PromoRowCard({ promo, onClick }: Props) {
  const { t } = useTranslation('lobby');
  // The headline sentences live in the promo namespace, with the card that
  // owns them; this tile only borrows them.
  const { t: tPromo } = useTranslation('promo');
  const currency = useMoneyCurrency();
  const formatMoney = useFormatMoney();
  const banner = promoBanner(promo);
  const { gradient, Icon } = KIND_THEME[promo.kind] ?? FALLBACK;

  return (
    <article className="shrink-0 w-[280px] snap-start scroll-ml-4 rounded-2xl overflow-hidden bg-[var(--bg-raised)] flex flex-col">
      {/* 16:9 — the shape promotion banners are uploaded in (BO promo-banner),
          so the banner shows whole and carries the card. */}
      <div
        className="relative aspect-video flex items-end p-3"
        style={banner ? { backgroundImage: `url(${banner})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: gradient }}
      >
        {banner ? (
          <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        ) : (
          <Icon aria-hidden size={44} className="absolute right-3 top-3 text-white/50" />
        )}
        <div className="relative text-white font-extrabold text-[15px] leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,.5)]">
          {rewardHeadlineFor(promo, currency, tPromo, (m) => formatMoney(m, currency))}
        </div>
      </div>

      <div className="flex flex-col gap-1 px-3 py-2.5 flex-1">
        <div className="text-[13px] font-semibold text-[var(--text-primary)] line-clamp-1">
          {promoTitle(promo)}
        </div>
        {promo.description ? (
          <p className="text-[11px] leading-snug text-[var(--text-secondary)] line-clamp-2">
            {promo.description}
          </p>
        ) : null}
        <Button size="sm" variant="primary" className="mt-auto h-8 w-full text-[12px]" onClick={onClick}>
          {t('landing.promotion.claim')}
        </Button>
      </div>
    </article>
  );
}
